import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import MaterialTable from "material-table";
import { ToastContainer, toast } from "react-toastify";
import ContentLoader from "react-content-loader";
import axios from "axios";

import authHeader from "../../services/auth-header";
import { API } from "../../backend";
import DeleteCompanyUserService from "../../services/user/deleteUserService";
import ActivateUserService from "../../services/user/activateUserService";
import AuthService from "../../services/auth.service";
import Helper from "../../Helper";
import { getTrans as t } from "../../Localization/translations";
import ActivateDeActivatedCustomerUserService from "../../services/user/customeruseractiveService";

// Same underlying data/endpoints as before (company/users/ for internal
// company users, company/customerusers/ | company/customeruserslist/ for
// customer-portal users), just normalized into one flat shape so both
// kinds can share a single "Active Users" / "Archived Users" MaterialTable
// pair with a Type column, instead of two separately-styled tables. The
// Archive/Reinstate action reuses the existing is_active toggle endpoints
// (ClientDelete for company users, CustomerActivateDeactivate for portal
// users) — both already safe, non-destructive is_active flips, just called
// in one direction per table instead of flip-flopping on a single table.

const toastOpts = {
  position: "top-right",
  autoClose: 1500,
  hideProgressBar: false,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
};

function ManageUser() {
  const customertable = useRef();
  const [items, setItems] = useState([]);
  const [customerusers, setCustomerUser] = useState([]);
  const customerPermission = localStorage.getItem("customer");

  const [successful, setSuccessful] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const handelDefault = () => {
    let url = API + "company/users/";
    axios.get(url, { headers: authHeader() }).then((response) => {
      setItems(response.data);
      setLoading(false);
    });
  };

  const handelDefaultcustomer = () => {
    let url = API + "company/customerusers/";
    axios.get(url, { headers: authHeader() }).then((response) => {
      setCustomerUser(response.data);
    });
  };

  const handlecustomer = () => {
    let url = API + "company/customeruserslist/";
    axios.get(url, { headers: authHeader() }).then((response) => {
      setCustomerUser(response.data);
      setLoading(false);
    });
  };

  const handleBan = (id, user_id, status) => {
    DeleteCompanyUserService.deleteUser(id, user_id, status).then((response) => {
      if (response.data.success) {
        setSuccessful(true);
        setCount(count + 1);
        toast.success(`${response.data.response}`, toastOpts);
      } else {
        toast.error(`${response.data.error}`, toastOpts);
        setSuccessful(false);
      }
    });
  };

  const handleActivation = (id) => {
    ActivateUserService.activateUser(id).then((response) => {
      if (response.data.success) {
        toast.success(`${response.data.response}`, toastOpts);
        setSuccessful(true);
      } else {
        toast.error(`${response.data.error}`, { ...toastOpts, autoClose: 2000 });
        setSuccessful(false);
      }
    });
  };

  const handleCustomerActivation = (id) => {
    ActivateDeActivatedCustomerUserService.ActiveDeactiveUser(id).then((response) => {
      if (response.data.success) {
        toast.success(`${response.data.response}`, toastOpts);
        setTimeout(() => {
          handelDefaultcustomer();
        }, 1000);
        setSuccessful(true);
      } else {
        toast.error(`${response.data.error}`, { ...toastOpts, autoClose: 2000 });
        setSuccessful(false);
      }
    });
  };

  const handleResetPassword = (email) => {
    if (!email) {
      toast.error("No email on file for this user.", toastOpts);
      return;
    }
    AuthService.resetpassword(email)
      .then((response) => {
        if (response.data && response.data.success) {
          toast.success(response.data.response || "Reset password email sent.", toastOpts);
        } else {
          toast.error((response.data && response.data.error) || "Couldn't send reset email.", toastOpts);
        }
      })
      .catch(() => toast.error("Couldn't send reset email.", toastOpts));
  };

  let navigate = useNavigate();
  const handleEdit = (id) => {
    navigate("/user/edituser", { state: id });
  };

  const handleCustomerEdit = (id) => {
    navigate("/user/editcustomeruser", { state: id });
  };

  useEffect(() => {
    if (customerPermission !== "Customer") {
      handelDefault();
      handelDefaultcustomer();
    } else {
      handlecustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // ── normalize both sources into one flat row shape ──────────────────────
  const companyRows = items.map((u) => ({
    key: `company-${u.id}`,
    kind: "company",
    raw: u,
    name: u.name,
    email: u.user_email,
    type: "User",
    active: !!u.user_status,
    dateArchived: u.date_deactivate,
  }));

  const portalRows = customerusers.map((u, idx) => ({
    key: `portal-${(u.user && u.user.id) || idx}`,
    kind: "portal",
    raw: u,
    name: [u.first_name, u.last_name].filter(Boolean).join(" ") || (u.user && u.user.email) || "",
    email: u.user ? u.user.email : "",
    type: "Portal",
    active: u.user ? !!u.user.is_active : false,
    dateArchived: null,
  }));

  const allRows = [...companyRows, ...portalRows];
  const activeRows = allRows.filter((r) => r.active);
  const archivedRows = allRows.filter((r) => !r.active);

  const editRow = (row) => {
    if (row.kind === "company") handleEdit(row.raw.id);
    else handleCustomerEdit(row.raw.user.id);
  };

  const toggleRow = (row) => {
    if (row.kind === "company") handleBan(row.raw.id, row.raw.user, row.raw.user_status);
    else handleCustomerActivation(row.raw.user.id);
  };

  const sendActivationForRow = (row) => {
    if (row.kind === "company") handleActivation(row.raw.user);
    else if (row.raw.user) handleActivation(row.raw.user.id);
  };

  const statusCell = (active) => (
    <div className={active ? "badge bg-gradient-success text-xxs" : "badge badge-sm bg-gradient-secondary"}>
      {active ? "Active" : "Archived"}
    </div>
  );

  const sharedTableOptions = {
    headerStyle: {
      backgroundColor: "white",
      color: "#245C7F",
      fontSize: 11,
    },
    rowStyle: (rowData, index) => {
      if (index % 2) {
        return { backgroundColor: "#fff" };
      }
    },
    sorting: true,
    search: false,
    toolbar: false,
    paging: true,
    pageSize: 5,
    emptyRowsWhenPaging: false,
    pageSizeOptions: [5, 12, 20, 50],
    paginationType: "stepped",
    showFirstLastPageButtons: false,
    actionsColumnIndex: -1,
  };

  const nameCol = (title) => ({
    title,
    field: "name",
    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
    cellStyle: { textAlign: "left", fontSize: 13 },
    render: (rowData) => (
      <>
        <div className="d-xl-none tc d-md-none d-lg-none">{title}</div>
        <div>{rowData.name}</div>
      </>
    ),
  });

  const emailCol = (title) => ({
    title,
    field: "email",
    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
    cellStyle: { textAlign: "left", fontSize: 12 },
    render: (rowData) => (
      <>
        <div className="d-xl-none tc d-md-none d-lg-none">{title}</div>
        <div>{rowData.email}</div>
      </>
    ),
  });

  const typeCol = {
    title: t("Type"),
    field: "type",
    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
    cellStyle: { textAlign: "left", fontSize: 12 },
    render: (rowData) => (
      <>
        <div className="d-xl-none tc d-md-none d-lg-none">{t("Type")}</div>
        <div>{rowData.type === "Portal" ? t("Portal") : t("User")}</div>
      </>
    ),
  };

  const statusCol = {
    title: t("Status"),
    field: "active",
    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
    cellStyle: { textAlign: "left", fontSize: 12 },
    render: (rowData) => (
      <>
        <div className="d-xl-none tc d-md-none d-lg-none">{t("Status")}</div>
        {statusCell(rowData.active)}
      </>
    ),
  };

  const dateArchivedCol = {
    title: t("Date Archived"),
    field: "dateArchived",
    headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
    cellStyle: { textAlign: "left", fontSize: 12 },
    render: (rowData) => (
      <>
        <div className="d-xl-none tc d-md-none d-lg-none">{t("Date Archived")}</div>
        <div>{rowData.dateArchived ? Helper.DateNormal(rowData.dateArchived) : "—"}</div>
      </>
    ),
  };

  return (
    <>
      <div className="">
        <ToastContainer />
        <div className="content container-fluid">
          {loading === false ? (
            <div className="row ">
              <div className="col-md-12">
                <div className="mb-0">
                  <div className="header pb-0 pt-3">
                    <div className="d-lg-flex">
                      <div>
                        <h5 className="mb-0 tc">{t("Active Users")}</h5>
                      </div>
                      {customerPermission !== "Customer" && (
                        <div className="ms-auto my-auto mt-lg-0 mt-4">
                          <div className="ms-auto my-auto">
                            <Link to="/user/adduser">
                              {" "}
                              <button
                                className="add mb-2 btn btn-primary mb-0"
                                data-bs-toggle="modal"
                                data-bs-target="#add_contact"
                              >
                                +&nbsp; {t("New User")}
                              </button>
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="body">
                    <div className="success">{/* {{msg}} */}</div>
                    <div className="p-0">
                      <MaterialTable
                        style={{
                          backgroundColor: "var(--bs-table-bg)",
                          borderBottomWidth: "1px",
                          boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                        }}
                        columns={[
                          nameCol(t("User Name")),
                          emailCol(t("User Email")),
                          typeCol,
                          statusCol,
                        ]}
                        data={activeRows}
                        localization={{
                          toolbar: { searchPlaceholder: "Search Party" },
                        }}
                        actions={[
                          {
                            icon: "edit",
                            tooltip: t("Edit User"),
                            onClick: (event, rowData) => editRow(rowData),
                          },
                          {
                            icon: "vpn_key",
                            tooltip: t("Reset Password"),
                            onClick: (event, rowData) => handleResetPassword(rowData.email),
                          },
                          (rowData) => ({
                            hidden: rowData.kind === "company" && rowData.raw.user_filed === "Company",
                            icon: "archive",
                            tooltip: t("Archive"),
                            onClick: (event, rowData) => toggleRow(rowData),
                          }),
                        ]}
                        options={sharedTableOptions}
                      />
                    </div>
                  </div>

                  <div className="header pb-0 pt-3 mt-4">
                    <div className="d-lg-flex">
                      <div>
                        <h5 className="mb-0 tc">{t("Archived Accounts Users")}</h5>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <MaterialTable
                      tableRef={customertable}
                      style={{
                        backgroundColor: "var(--bs-table-bg)",
                        borderBottomWidth: "1px",
                        boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                      }}
                      columns={[
                        nameCol(t("Account Name")),
                        emailCol(t("Owner")),
                        dateArchivedCol,
                        typeCol,
                        statusCol,
                      ]}
                      data={archivedRows}
                      localization={{
                        toolbar: { searchPlaceholder: "Search Party" },
                      }}
                      actions={[
                        {
                          icon: "unarchive",
                          tooltip: t("Reinstate"),
                          onClick: (event, rowData) => toggleRow(rowData),
                        },
                        (rowData) => ({
                          hidden: customerPermission === "Customer" || rowData.kind !== "company",
                          icon: () => (
                            <span style={{ color: "green" }}>
                              <i className="fa fa-solid fa-paper-plane" />
                            </span>
                          ),
                          tooltip: t("Send Activation Link"),
                          onClick: (event, rowData) => sendActivationForRow(rowData),
                        }),
                      ]}
                      options={sharedTableOptions}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ContentLoader
              viewBox="0 0 1000 550"
              backgroundColor="rgba(0,0,0,0.06)"
              foregroundColor="rgba(0,0,0,0.12)"
            >
              <rect x="51" y="45" rx="3" ry="3" width="906" height="17" />
              <circle cx="879" cy="123" r="11" />
              <circle cx="914" cy="123" r="11" />
              <rect x="104" y="115" rx="3" ry="3" width="141" height="15" />
              <rect x="305" y="114" rx="3" ry="3" width="299" height="15" />
              <rect x="661" y="114" rx="3" ry="3" width="141" height="15" />
              <rect x="55" y="155" rx="3" ry="3" width="897" height="2" />
              <circle cx="880" cy="184" r="11" />
              <circle cx="915" cy="184" r="11" />
              <rect x="105" y="176" rx="3" ry="3" width="141" height="15" />
              <rect x="306" y="175" rx="3" ry="3" width="299" height="15" />
              <rect x="662" y="175" rx="3" ry="3" width="141" height="15" />
              <rect x="56" y="216" rx="3" ry="3" width="897" height="2" />
              <circle cx="881" cy="242" r="11" />
              <circle cx="916" cy="242" r="11" />
              <rect x="106" y="234" rx="3" ry="3" width="141" height="15" />
              <rect x="307" y="233" rx="3" ry="3" width="299" height="15" />
              <rect x="663" y="233" rx="3" ry="3" width="141" height="15" />
              <rect x="57" y="274" rx="3" ry="3" width="897" height="2" />
              <circle cx="882" cy="303" r="11" />
              <circle cx="917" cy="303" r="11" />
              <rect x="107" y="295" rx="3" ry="3" width="141" height="15" />
              <rect x="308" y="294" rx="3" ry="3" width="299" height="15" />
              <rect x="664" y="294" rx="3" ry="3" width="141" height="15" />
              <rect x="58" y="335" rx="3" ry="3" width="897" height="2" />
              <circle cx="881" cy="363" r="11" />
              <circle cx="916" cy="363" r="11" />
              <rect x="106" y="355" rx="3" ry="3" width="141" height="15" />
              <rect x="307" y="354" rx="3" ry="3" width="299" height="15" />
              <rect x="663" y="354" rx="3" ry="3" width="141" height="15" />
              <rect x="57" y="395" rx="3" ry="3" width="897" height="2" />
              <circle cx="882" cy="424" r="11" />
              <circle cx="917" cy="424" r="11" />
              <rect x="107" y="416" rx="3" ry="3" width="141" height="15" />
              <rect x="308" y="415" rx="3" ry="3" width="299" height="15" />
              <rect x="664" y="415" rx="3" ry="3" width="141" height="15" />
              <rect x="55" y="453" rx="3" ry="3" width="897" height="2" />
              <rect x="51" y="49" rx="3" ry="3" width="2" height="465" />
              <rect x="955" y="49" rx="3" ry="3" width="2" height="465" />
              <circle cx="882" cy="484" r="11" />
              <circle cx="917" cy="484" r="11" />
              <rect x="107" y="476" rx="3" ry="3" width="141" height="15" />
              <rect x="308" y="475" rx="3" ry="3" width="299" height="15" />
              <rect x="664" y="475" rx="3" ry="3" width="141" height="15" />
              <rect x="55" y="513" rx="3" ry="3" width="897" height="2" />
              <rect x="52" y="80" rx="3" ry="3" width="906" height="17" />
              <rect x="53" y="57" rx="3" ry="3" width="68" height="33" />
              <rect x="222" y="54" rx="3" ry="3" width="149" height="33" />
              <rect x="544" y="55" rx="3" ry="3" width="137" height="33" />
              <rect x="782" y="56" rx="3" ry="3" width="72" height="33" />
              <rect x="933" y="54" rx="3" ry="3" width="24" height="33" />
            </ContentLoader>
          )}
        </div>
      </div>

      <div
        className="modal right fade"
        id="edit_contact"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      ></div>
    </>
  );
}

export default ManageUser;
